import { useMutation, useQueries, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { projectSettingsApi } from '@/api/projectSettings'
import { workItemsApi } from '@/api/workItems'
import { useTenantKey } from './useTenantKey'
import type { AzureDevOpsField, SaveAzureDevOpsIngestionSettingRequest } from '@/types'

export function useAzureDevOpsIngestionSetting(projectId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'settings', 'azure-devops-ingestion'),
    queryFn: () => projectSettingsApi.getAzureDevOpsIngestionSetting(projectId!),
    enabled: !!projectId,
  })
}

export function useSaveAzureDevOpsIngestionSetting(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (request: SaveAzureDevOpsIngestionSettingRequest) =>
      projectSettingsApi.saveAzureDevOpsIngestionSetting(projectId!, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'settings', 'azure-devops-ingestion') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-ingestion-status') })
    },
  })
}

export function useAzureDevOpsTeams(projectId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-teams'),
    queryFn: () => workItemsApi.getAzureDevOpsTeams(projectId!),
    enabled: !!projectId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  })
}

function intersectFields(lists: (AzureDevOpsField[] | undefined)[]): AzureDevOpsField[] {
  if (lists.length === 0 || lists.some(list => list === undefined)) return []
  const [first, ...rest] = lists as AzureDevOpsField[][]
  return first.filter(field => rest.every(list => list.some(f => f.referenceName === field.referenceName)))
}

function combineFieldQueries(results: UseQueryResult<AzureDevOpsField[]>[]) {
  return {
    fields: intersectFields(results.map(r => r.data)),
    isLoading: results.some(r => r.isLoading),
    error: results.find(r => r.error)?.error ?? null,
  }
}

export function useAzureDevOpsCommonWorkItemTypeFields(projectId: string | undefined, workItemTypes: string[]) {
  const tenantKey = useTenantKey()
  return useQueries({
    queries: workItemTypes.map(workItemType => ({
      queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-type-fields', workItemType),
      queryFn: () => workItemsApi.getAzureDevOpsWorkItemTypeFields(projectId!, workItemType),
      enabled: !!projectId,
      staleTime: 10 * 60 * 1000,
      retry: false,
    })),
    combine: combineFieldQueries,
  })
}

export function useAzureDevOpsIngestionStatus(projectId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-ingestion-status'),
    queryFn: () => workItemsApi.getAzureDevOpsIngestionStatus(projectId!),
    enabled: !!projectId,
    staleTime: 60 * 1000,
    retry: false,
  })
}
